/**
 * BundleStateBadge — per-runtime lextools-bundle state vs the platform's
 * ACTIVE bundle (Step 7 of ai-docs/lextools-idle-restart-activation-plan.md).
 *
 * Renders the per-row lextools-bundle state on the LexPortal RuntimeFleet
 * page. Source of truth: lexserver `GET /api/platform/runtimes/versions`
 * (the per-row `bundleTarget` + `bundlePending` + `running.bundle` fields).
 *
 * Wire state literals stay stable for back-compat; user-facing labels use
 * "active version" semantics — a runtime is either ON the platform's
 * currently-active bundle, DIFFERS from it (rollback ⇒ older active is
 * still "active", not "outdated"), pending an idle-restart, or in one
 * of the indeterminate / no-target buckets.
 *
 *   1. on-active     — running.bundle.tag === bundleTarget.tag AND
 *                       (sha256 missing OR sha256 matches)
 *                       green, "On active version — bundle X"
 *   2. pending       — bundlePending != null
 *                       yellow, "Pending T1-idle since HH:MM UTC"
 *   3. differs       — bundleTarget != null && running.bundle differs
 *                       amber, "Differs from active (X)"
 *   4. indeterminate — running == null OR no bundle reported, with
 *                       bundleTarget != null
 *                       grey, "bundle state unknown"
 *   5. no-target     — bundleTarget == null && bundlePending == null
 *                       slate, "—"
 *
 * **Precedence:** `pending` wins over `no-target` (an admin could clear
 * the head while a job is still in-flight; the operationally-relevant
 * state is always "a job is running").
 *
 * Hover detail uses the Radix Tooltip primitive (a11y-safe; supports
 * keyboard, screen reader, multi-line content). The native title= attr
 * is NOT used — it's silently broken on Safari + iOS + screen readers.
 */

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type {
  BundlePendingSummary,
  BundleTargetSummary,
  RunningRuntimeBundle,
} from "@/types/platform";

type BundleState =
  | "on-active"
  | "pending"
  | "differs"
  | "indeterminate"
  | "no-target";

type BundleStateBadgeProps = {
  bundleTarget?: BundleTargetSummary | null;
  bundlePending?: BundlePendingSummary | null;
  runningBundle?: RunningRuntimeBundle | null;
  className?: string;
};

const styles: Record<BundleState, string> = {
  "on-active":
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800",
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
  differs:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  indeterminate:
    "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  "no-target":
    "bg-slate-50 text-slate-500 dark:bg-slate-900 dark:text-slate-400 border-slate-200 dark:border-slate-800",
};

function classify(
  bundleTarget: BundleTargetSummary | null | undefined,
  bundlePending: BundlePendingSummary | null | undefined,
  runningBundle: RunningRuntimeBundle | null | undefined,
): BundleState {
  // pending wins over no-target: an admin could clear the head while a
  // job is still queued, and the operationally-relevant state is always
  // "a job is running."
  if (bundlePending) return "pending";
  if (!bundleTarget) return "no-target";
  if (!runningBundle) return "indeterminate";
  // SHA match uses `bundleTarget.contentSha256`
  // (the manifest.bundle_sha256 the publisher stamped) because the
  // runtime reports the SAME hash back in `runningBundle.sha256` — both
  // are the Merkle sum of the files inside the tarball. Comparing
  // against `bundleTarget.sha256` (the wrapper-tarball sha) instead is
  // the bug this code shipped with before the 2026-06-10 dual-sha fix:
  // those are two different shas of two different things and never
  // matched, so every healthy fleet showed "Differs from active".
  //
  // Fallbacks for the sha-comparison side:
  //   - runningBundle.sha256 == null  → older runtime / v1 manifest
  //     where the loader didn't extract bundle_sha256. Trust the tag.
  //   - bundleTarget.contentSha256 == null  → bundle was promoted
  //     before the dual-sha fix landed (or the backend's manifest
  //     extraction failed for that promote). Trust the tag.
  // Both fallbacks degrade to tag-only — which is exactly what the
  // operator means by "is the tag installed?" when no sha is available.
  if (runningBundle.tag !== bundleTarget.tag) return "differs";
  const runningSha = runningBundle.sha256;
  const targetContentSha = bundleTarget.contentSha256;
  if (runningSha == null || targetContentSha == null) return "on-active";
  if (runningSha === targetContentSha) return "on-active";
  return "differs";
}

/** UTC HH:MM formatter — explicit UTC label avoids the multi-timezone
 *  operator ambiguity flagged in the workflow review. */
function formatUtcTime(epochMs: number): string {
  const d = new Date(epochMs);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m} UTC`;
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60}m`;
}

function label(
  state: BundleState,
  bundleTarget: BundleTargetSummary | null | undefined,
  bundlePending: BundlePendingSummary | null | undefined,
): string {
  switch (state) {
    case "on-active":
      return `On active version — bundle ${bundleTarget?.tag ?? "?"}`;
    case "pending": {
      const since = bundlePending?.busySinceMs;
      const sinceStr = since ? formatUtcTime(since) : "—";
      return `Pending T1-idle since ${sinceStr}`;
    }
    case "differs":
      return `Differs from active (${bundleTarget?.tag ?? "?"})`;
    case "indeterminate":
      return "bundle state unknown";
    case "no-target":
      return "—";
  }
}

/** Detail lines for the tooltip body. Multi-line is rendered as <div>s
 *  inside TooltipContent (Tooltip primitive handles newlines correctly). */
function detailLines(
  state: BundleState,
  bundleTarget: BundleTargetSummary | null | undefined,
  bundlePending: BundlePendingSummary | null | undefined,
  runningBundle: RunningRuntimeBundle | null | undefined,
): string[] {
  switch (state) {
    case "on-active":
      return runningBundle
        ? [
            `tag: ${runningBundle.tag}`,
            `sha256: ${runningBundle.sha256 ?? "(none)"}`,
            `installed: ${runningBundle.installed_at}`,
            `source: ${runningBundle.source}`,
          ]
        : [];
    case "pending": {
      if (!bundlePending) return [];
      const lines = [
        `target tag: ${bundlePending.bundleId}`,
        `state: ${bundlePending.state}`,
        `attempts: ${bundlePending.attempts}`,
      ];
      if (bundlePending.busySinceMs) {
        lines.push(
          `busy for: ${formatDuration(Date.now() - bundlePending.busySinceMs)}`,
        );
      }
      if (bundlePending.lastSignature) {
        lines.push(`last reason: ${bundlePending.lastSignature}`);
      }
      return lines;
    }
    case "differs":
      return bundleTarget && runningBundle
        ? [
            `running: ${runningBundle.tag}`,
            `active: ${bundleTarget.tag}`,
            `promoted: ${bundleTarget.promoted_at}`,
            "No bundle-install job enqueued yet — operator must promote with activate.",
          ]
        : [];
    case "indeterminate":
      return bundleTarget
        ? [
            `target: ${bundleTarget.tag}`,
            `promoted: ${bundleTarget.promoted_at}`,
            "Runtime hasn't reported a bundle (older image, unreachable, or pre-Step-2 deploy).",
          ]
        : [];
    case "no-target":
      return [
        "No lextools bundle has been promoted yet.",
        "Operators run install-bundle.sh or LexPortal Promote.",
      ];
  }
}

export function BundleStateBadge({
  bundleTarget,
  bundlePending,
  runningBundle,
  className,
}: BundleStateBadgeProps) {
  const state = classify(bundleTarget, bundlePending, runningBundle);
  const labelText = label(state, bundleTarget, bundlePending);
  const lines = detailLines(state, bundleTarget, bundlePending, runningBundle);
  const ariaContext =
    lines.length > 0 ? `${labelText}. ${lines.join("; ")}` : labelText;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={cn(styles[state], className)}
            data-testid="bundle-state-badge"
            data-bundle-state={state}
            aria-label={ariaContext}
            tabIndex={0}
          >
            {labelText}
          </Badge>
        </TooltipTrigger>
        {lines.length > 0 ? (
          <TooltipContent side="top" align="start" className="max-w-xs">
            <div className="flex flex-col gap-0.5 text-xs">
              {lines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </TooltipContent>
        ) : null}
      </Tooltip>
    </TooltipProvider>
  );
}
